<template>
  <div class="campaigns-page">
    <!-- Modern Header matching Forms/Wiki/Workflow style -->
    <div class="campaigns-header">
      <div class="header-left">
        <h1 class="page-title">Campaigns</h1>
      </div>
      <div class="header-actions">
        <b-button
          v-if="isAiRecallEnabled"
          variant="outline-secondary"
          class="mr-2"
          title="Configure the AI Recall email follow-up for this office"
          @click="showAiRecallCadence = true"
        >
          <i class="fa fa-cog"></i> AI Recall Settings
        </b-button>
        <!-- AI features aren't launched: show the entry point as an upsell, but nothing opens (2026-09-16). -->
        <b-button
          v-else-if="aiSettingsLoaded"
          data-test="ai-recall-coming-soon"
          variant="outline-secondary"
          class="mr-2 ai-coming-soon-btn"
          title="AI Recall is coming soon"
          disabled
        >
          <i class="fa fa-cog"></i> AI Recall Settings
          <span class="badge badge-light ml-1">Coming soon</span>
        </b-button>
        <b-button variant="success" class="create-btn" @click="openCreateCampaignModal">
          <i class="fa fa-plus"></i> New Campaign
        </b-button>
      </div>
    </div>

    <!-- Gated too, not just the button: without this the modal stays mounted and a
         stray showAiRecallCadence=true (devtools, a future code path) would open an
         editor the site is not entitled to. -->
    <AiRecallCadenceModal
      v-if="isAiRecallEnabled"
      v-model="showAiRecallCadence"
      :site-id="siteID"
      :all-settings="allSettings"
    />

    <CampaignsSkeleton v-if="busy" />

    <!-- Sub-navigation for Campaigns section -->
    <div class="campaigns-subnav" v-show="!busy">
      <router-link
        to="/app/campaigns"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns' }"
      >
        <i class="fa fa-bullhorn"></i>
        <span>Campaigns</span>
      </router-link>
      <router-link
        to="/app/campaigns-drafts"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns-drafts' }"
      >
        <i class="fa fa-pencil-square-o"></i>
        <span>Drafts</span>
      </router-link>
      <router-link
        to="/app/campaigns-statistics"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns-statistics' }"
      >
        <i class="fa fa-bar-chart"></i>
        <span>Reports</span>
      </router-link>
      <router-link
        to="/app/campaigns-templates"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns-templates' }"
      >
        <i class="fa fa-file-text-o"></i>
        <span>Templates</span>
      </router-link>
      <router-link
        to="/app/campaigns-ab-tests"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns-ab-tests' }"
      >
        <i class="fa fa-flask"></i>
        <span>A/B Tests</span>
      </router-link>
    </div>

    <ErrorDisplay v-show="!busy" />

    <!-- Stats Overview Cards -->
    <div class="stats-overview" v-if="campaignStats" v-show="!busy">
      <div class="stat-card">
        <div class="stat-icon bg-primary-light">
          <i class="fa fa-paper-plane text-primary"></i>
        </div>
        <div class="stat-content">
          <h3 class="stat-number">{{ campaignStats.totalCampaigns | formatNumber }}</h3>
          <p class="stat-label">Total Campaigns</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon bg-success-light">
          <i class="fa fa-check-circle text-success"></i>
        </div>
        <div class="stat-content">
          <h3 class="stat-number">{{ campaignStats.messagesSent | formatNumber }}</h3>
          <p class="stat-label">Messages Sent</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon bg-info-light">
          <i class="fa fa-users text-info"></i>
        </div>
        <div class="stat-content">
          <h3 class="stat-number">{{ campaignStats.totalRecipients | formatNumber }}</h3>
          <p class="stat-label">Total Recipients</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon bg-warning-light">
          <i class="fa fa-percent text-warning"></i>
        </div>
        <div class="stat-content">
          <h3 class="stat-number">{{ campaignStats.avgDeliveryRate }}%</h3>
          <p class="stat-label">Avg. Delivery Rate</p>
        </div>
      </div>
    </div>

    <!-- Campaigns Panel -->
    <div class="campaigns-panel" v-show="!busy">
      <div class="panel-card">
        <div class="panel-header">
          <h5 class="section-title">
            <i class="fa fa-bullhorn section-icon"></i>
            Your Campaigns
          </h5>
          <div class="view-toggle">
            <button
              class="view-btn"
              :class="{ active: viewMode === 'list' }"
              @click="viewMode = 'list'"
              title="List View"
            >
              <i class="fa fa-list"></i>
            </button>
            <button
              class="view-btn"
              :class="{ active: viewMode === 'grid' }"
              @click="viewMode = 'grid'"
              title="Grid View"
            >
              <i class="fa fa-th-large"></i>
            </button>
          </div>
        </div>

        <!-- Filter Row -->
        <div class="filter-row">
          <div class="filter-container">
            <div class="search-box">
              <i class="fa fa-search search-icon"></i>
              <input
                type="text"
                v-model="searchQuery"
                placeholder="Search campaigns..."
                class="search-input"
                @input="debouncedSearch"
              />
              <i
                v-if="searchQuery"
                class="fa fa-times-circle clear-icon"
                @click="clearSearch"
                title="Clear search"
              ></i>
            </div>
          </div>

          <div class="filter-dropdowns">
            <select v-model="filterStatus" class="filter-select">
              <option :value="null">All Status</option>
              <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                {{ option.text }}
              </option>
            </select>
            <select v-model="filterType" class="filter-select">
              <option :value="null">All Types</option>
              <option v-for="option in typeOptions" :key="option.value" :value="option.value">
                {{ option.text }}
              </option>
            </select>
            <select v-model="filterList" class="filter-select">
              <option :value="null">All Lists</option>
              <option v-for="list in patientLists" :key="list.value" :value="list.value">
                {{ list.text }}
              </option>
            </select>
          </div>

          <div class="quick-filters">
            <button
              class="quick-filter-btn"
              :class="{ active: activeQuickFilter === 'all' }"
              @click="setQuickFilter('all')"
            >
              <i class="fa fa-inbox"></i> All
            </button>
            <button
              class="quick-filter-btn"
              :class="{ active: activeQuickFilter === 'active' }"
              @click="setQuickFilter('active')"
            >
              <i class="fa fa-play-circle"></i> Active
            </button>
            <button
              class="quick-filter-btn"
              :class="{ active: activeQuickFilter === 'draft' }"
              @click="setQuickFilter('draft')"
            >
              <i class="fa fa-edit"></i> Drafts
            </button>
            <button
              class="quick-filter-btn"
              :class="{ active: activeQuickFilter === 'completed' }"
              @click="setQuickFilter('completed')"
            >
              <i class="fa fa-check"></i> Completed
            </button>
          </div>
        </div>

        <!-- Active filter indicator -->
        <div class="active-filters" v-if="hasFilters">
          <span class="filter-info">
            <i class="fa fa-filter"></i>
            Showing {{ filteredCampaigns.length }} campaign{{ filteredCampaigns.length !== 1 ? 's' : '' }}
            <span v-if="searchQuery" class="active-filter-tag">Search: "{{ searchQuery }}"</span>
            <span v-if="filterStatus" class="active-filter-tag">Status: {{ filterStatus }}</span>
            <span v-if="filterType" class="active-filter-tag">Type: {{ filterType }}</span>
          </span>
          <button class="clear-filter-btn" @click="clearFilters">
            <i class="fa fa-times"></i> Clear Filters
          </button>
        </div>

        <!-- Empty State -->
        <div class="empty-state" v-if="!busy && filteredCampaigns.length === 0">
          <div class="empty-icon">
            <i class="fa fa-bullhorn"></i>
          </div>
          <h4>No Campaigns Found</h4>
          <p v-if="hasFilters">Try adjusting your filters or search query</p>
          <p v-else>Get started by creating your first campaign</p>
          <button
            v-if="!hasFilters"
            class="btn btn-primary create-first-btn"
            @click="openCreateCampaignModal"
          >
            <i class="fa fa-plus"></i> Create Campaign
          </button>
          <button v-else class="btn btn-outline-secondary" @click="clearFilters">
            <i class="fa fa-times"></i> Clear Filters
          </button>
        </div>

        <!-- List View -->
        <div v-if="viewMode === 'list' && filteredCampaigns.length > 0" class="campaigns-table-container">
          <!-- Table Header -->
          <div class="campaigns-table-header campaigns-table-header-extended">
            <div class="sortable" @click="sortBy('Name')">
              Campaign
              <i :class="getSortIcon('Name')"></i>
            </div>
            <div>Type</div>
            <div class="sortable" @click="sortBy('Status')">
              Status
              <i :class="getSortIcon('Status')"></i>
            </div>
            <div>Recipients</div>
            <div>Delivered</div>
            <div>Open Rate</div>
            <div>Click Rate</div>
            <div class="sortable" @click="sortBy('CreatedAt')">
              Created
              <i :class="getSortIcon('CreatedAt')"></i>
            </div>
            <div class="text-right">Actions</div>
          </div>

          <!-- Campaign Items -->
          <div
            class="campaign-item campaign-item-extended"
            v-for="campaign in paginatedCampaigns"
            :key="campaign.CampaignID"
            @click="viewCampaign(campaign)"
          >
            <!-- Name Column -->
            <div class="name-column">
              <i class="fa fa-bullhorn campaign-icon"></i>
              <div class="campaign-name">{{ campaign.Name }}</div>
            </div>

            <!-- Type Column -->
            <div class="type-column">
              <span class="type-badge" :class="getTypeBadgeClass(campaign.Type)">
                <i :class="getTypeIcon(campaign.Type)"></i>
                {{ campaign.Type }}
              </span>
            </div>

            <!-- Status Column -->
            <div class="status-column">
              <span class="status-badge" :class="'status-' + (campaign.Status || '').toLowerCase()">
                {{ campaign.Status | capitalize }}
              </span>
            </div>

            <!-- Recipients Column -->
            <div class="recipients-column">
              {{ (campaign.Type === 'Both' ? campaign.UniquePatientCount : campaign.RecipientCount) | formatNumber }}
            </div>

            <!-- Delivered Column -->
            <div class="delivered-column">
              <span class="delivered-count">{{ campaign.DeliveredCount | formatNumber }}</span>
              <span class="delivery-rate">({{ getDeliveryRate(campaign) }}%)</span>
            </div>

            <!-- Open Rate Column -->
            <div class="rate-column">
              <span v-if="campaign.Type === 'Email' || campaign.Type === 'Both'" class="rate-value">
                {{ getOpenRate(campaign) }}%
              </span>
              <span v-else class="rate-na">-</span>
            </div>

            <!-- Click Rate Column -->
            <div class="rate-column">
              <span class="rate-value">
                {{ getClickRate(campaign) }}%
              </span>
            </div>

            <!-- Created Column -->
            <div class="date-column">
              {{ campaign.CreatedAt | dateformat(dateFormatStr) }}
            </div>

            <!-- Actions Column - Enhanced Dropdown Menu -->
            <div class="actions-container" @click.stop>
              <b-dropdown
                variant="link"
                no-caret
                right
                toggle-class="action-menu-toggle"
              >
                <template #button-content>
                  <i class="fa fa-ellipsis-v"></i>
                </template>

                <b-dropdown-item @click="viewCampaign(campaign)">
                  <i class="fa fa-eye mr-2"></i> View Details
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="['draft', 'list', 'failed'].includes((campaign.Status || '').toLowerCase())"
                  @click="editCampaign(campaign)"
                >
                  <i class="fa fa-pencil mr-2"></i> Edit
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="(campaign.Status || '').toLowerCase() === 'draft'"
                  @click="confirmSendCampaign(campaign)"
                >
                  <i class="fa fa-paper-plane mr-2"></i> Send
                </b-dropdown-item>

                <b-dropdown-item @click="duplicateCampaign(campaign)">
                  <i class="fa fa-copy mr-2"></i> Reuse (Duplicate)
                </b-dropdown-item>

                <b-dropdown-divider></b-dropdown-divider>

                <b-dropdown-item
                  @click="confirmDeleteCampaign(campaign)"
                  class="text-danger"
                >
                  <i class="fa fa-trash mr-2"></i> Delete
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </div>

        <!-- Grid View -->
        <div v-if="viewMode === 'grid' && filteredCampaigns.length > 0" class="campaigns-grid">
          <CampaignCard
            v-for="campaign in paginatedCampaigns"
            :key="campaign.CampaignID"
            :campaign="campaign"
            @view="viewCampaign"
            @edit="editCampaign"
            @duplicate="duplicateCampaign"
            @delete="confirmDeleteCampaign"
            @send="sendCampaign"
          />
        </div>


        <!-- Pagination -->
        <div class="pagination-container" v-if="totalPages > 1 || totalItems > 10">
          <div class="pagination-info">
            Showing {{ startItem }}-{{ endItem }} of {{ totalItems }}
          </div>
          <div class="pagination-controls">
            <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage = 1">
              <i class="fa fa-angle-double-left"></i>
            </button>
            <button class="pagination-btn" :disabled="currentPage === 1" @click="previousPage">
              <i class="fa fa-angle-left"></i>
            </button>
            <span class="pagination-page">Page {{ currentPage }} of {{ totalPages }}</span>
            <button class="pagination-btn" :disabled="currentPage === totalPages" @click="nextPage">
              <i class="fa fa-angle-right"></i>
            </button>
            <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
              <i class="fa fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Campaign Modal -->
    <CreateCampaignModal
      ref="createCampaignModal"
      :campaign="selectedCampaign"
      :is-editing="isEditing"
      @saved="onCampaignSaved"
    />

    <!-- Over-allowance charge approval for quick-send from the list -->
    <overage-approval-modal ref="overageModal" />

    <!-- Delete Confirmation Modal -->
    <b-modal ref="delete" id="delete-campaign-modal" title="Delete Campaign" body-class="bg-white" centered>
      <div class="delete-modal-content">
        <i class="fa fa-exclamation-triangle warning-icon"></i>
        <p>Are you sure you want to delete the campaign <strong>"{{ campaignToDelete?.Name }}"</strong>?</p>
        <p class="warning-text">This action cannot be undone.</p>
      </div>
      <div slot="modal-footer" class="modal-footer-btns">
        <b-button variant="outline-secondary" @click="$refs.delete.hide()">Cancel</b-button>
        <b-button variant="danger" @click="deleteCampaign">Delete</b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import ErrorDisplay from '@/components/ErrorDisplay/ErrorDisplay';
import CampaignsSkeleton from './CampaignsSkeleton.vue';
import CampaignCard from './components/CampaignCard.vue';
import CreateCampaignModal from './components/CreateCampaignModal.vue';
import { buildQuickSendParams, campaignAudienceCount, isAiCallCampaign } from '@/utils/campaignSend';
import { OVERAGE_PRICE_PER_SEGMENT_USD } from '@/utils/smsSegments';
import { interpretSendGate, buildVolumeConfirmMessage, buildPaymentFailedMessage } from '@/utils/campaignGate';
import OverageApprovalModal from './components/OverageApprovalModal.vue';
import AiRecallCadenceModal from './components/AiRecallCadenceModal.vue';
import mixin from '../../mixin.js';
import debounce from 'lodash/debounce';

export default {
  name: 'Campaigns',
  components: {
    ErrorDisplay,
    CampaignsSkeleton,
    CampaignCard,
    CreateCampaignModal,
    OverageApprovalModal,
    AiRecallCadenceModal
  },
  mixins: [mixin],

  data() {
    return {
      showAiRecallCadence: false,
      busy: false,
      searchQuery: '',
      filterStatus: null,
      filterType: null,
      filterList: null,
      viewMode: 'list',
      currentPage: 1,
      perPage: 10,
      activeQuickFilter: 'all',
      selectedCampaign: null,
      isEditing: false,
      campaignToDelete: null,
      sortField: 'CreatedAt',
      sortDirection: 'desc',


      // Patient lists for filter
      patientLists: [
        { value: 'list1', text: 'Active Patients' },
        { value: 'list2', text: 'Recall Due' },
        { value: 'list3', text: 'New Patients' },
        { value: 'list4', text: 'Treatment Plan Pending' }
      ],


      statusOptions: [
        { value: 'Draft', text: 'Draft' },
        { value: 'Scheduled', text: 'Scheduled' },
        { value: 'Active', text: 'Active' },
        { value: 'Completed', text: 'Completed' },
        { value: 'Paused', text: 'Paused' }
      ],

      typeOptions: [
        { value: 'SMS', text: 'SMS' },
        { value: 'Email', text: 'Email' },
        { value: 'Both', text: 'SMS & Email' }
      ]
    };
  },

  computed: {
    ...mapState('customer', ['siteID', 'settings']),

    allSettings() {
      const customerState = this.$store.state.customer;
      return customerState?.allSettings || null;
    },

    // Site settings have arrived — until then we can't tell "not enabled" from "not loaded yet",
    // so the Coming soon placeholder waits (a paying site must never flash it).
    aiSettingsLoaded() {
      return !!(this.allSettings && Object.keys(this.allSettings).length)
        || (Array.isArray(this.settings) && this.settings.length > 0);
    },

    // Is the AI feature paid for on this site? Gates the AI Recall Settings entry
    // point, so practices without the AI feature never see a switch for it.
    //
    // Deliberately the SAME key that gates AI Call campaigns (AI_FOLLOWUP_PAID_FOR)
    // and the same read shape as CreateCampaignModal's isAiCallEnabled — including
    // the raw-settings-array fallback, because allSettings is not always populated
    // when this page first renders. Defaults to FALSE on anything unexpected: an
    // ungated AI control is worse than a hidden one.
    isAiRecallEnabled() {
      try {
        let paidValue = null;
        const allSettings = this.allSettings;
        const settingsArray = this.settings;

        if (allSettings && allSettings.AI_FOLLOWUP_PAID_FOR) {
          paidValue = allSettings.AI_FOLLOWUP_PAID_FOR.PropertyValue;
        } else if (Array.isArray(settingsArray)) {
          const paidSetting = settingsArray.find((s) => s.Property === 'AI_FOLLOWUP_PAID_FOR');
          if (paidSetting) paidValue = paidSetting.PropertyValue;
        }

        if (!paidValue) return false;
        return String(paidValue).trim().toUpperCase() === 'TRUE';
      } catch (error) {
        console.error('[Campaigns] Error checking isAiRecallEnabled:', error);
        return false;
      }
    },

    dateFormatStr() {
      return this.settings?.GENERAL_DATE_FORMAT?.PropertyValue || 'MM/DD/YYYY';
    },

    campaigns() {
      const raw = this.$store.state.campaigns.campaigns || [];
      return raw.map(c => ({
        ...c,
        RecipientCount: c.RecipientCount || c.TotalRecipients || 0,
        UniquePatientCount: c.UniquePatientCount || 0,
        DeliveredCount: c.DeliveredCount || c.TotalDelivered || 0,
        OpenCount: c.OpenCount || c.TotalOpened || 0,
        ClickCount: c.ClickCount || c.TotalClicked || 0
      }));
    },

    campaignStats() {
      const all = this.campaigns;
      const totalRecipients = all.reduce((sum, c) => sum + (c.Type === 'Both' ? (c.UniquePatientCount || 0) : (c.RecipientCount || 0)), 0);
      const messagesSent = all.reduce((sum, c) => sum + (c.SentCount || 0), 0);
      const totalDelivered = all.reduce((sum, c) => sum + (c.DeliveredCount || 0), 0);
      const avgDeliveryRate = messagesSent > 0 ? ((totalDelivered / messagesSent) * 100).toFixed(1) : 0;
      return {
        totalCampaigns: all.length,
        messagesSent,
        totalRecipients,
        avgDeliveryRate
      };
    },

    hasFilters() {
      return this.searchQuery || this.filterStatus || this.filterType || this.filterList || this.activeQuickFilter !== 'all';
    },

    sortedCampaigns() {
      const campaigns = [...this.campaigns];
      return campaigns.sort((a, b) => {
        let aVal = a[this.sortField];
        let bVal = b[this.sortField];

        if (this.sortField === 'Name' || this.sortField === 'Status') {
          aVal = (aVal || '').toLowerCase();
          bVal = (bVal || '').toLowerCase();
        }

        if (this.sortField === 'CreatedAt') {
          aVal = new Date(aVal || 0);
          bVal = new Date(bVal || 0);
        }

        if (this.sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    },

    filteredCampaigns() {
      let filtered = [...this.sortedCampaigns];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(c =>
          c.Name.toLowerCase().includes(query) ||
          c.Description?.toLowerCase().includes(query)
        );
      }

      // Status filter
      if (this.filterStatus) {
        const fs = this.filterStatus.toLowerCase();
        filtered = filtered.filter(c => (c.Status || '').toLowerCase() === fs);
      }

      // Type filter
      if (this.filterType) {
        const ft = this.filterType.toLowerCase();
        filtered = filtered.filter(c => (c.Type || '').toLowerCase() === ft);
      }

      // Quick filter
      if (this.activeQuickFilter !== 'all') {
        switch (this.activeQuickFilter) {
          case 'active':
            filtered = filtered.filter(c => {
              const s = (c.Status || '').toLowerCase();
              return s === 'active' || s === 'scheduled' || s === 'sending';
            });
            break;
          case 'draft':
            filtered = filtered.filter(c => {
              const s = (c.Status || '').toLowerCase();
              return s === 'draft' || s === 'list';
            });
            break;
          case 'completed':
            filtered = filtered.filter(c => {
              const s = (c.Status || '').toLowerCase();
              return s === 'completed' || s === 'sent';
            });
            break;
        }
      }

      return filtered;
    },

    paginatedCampaigns() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredCampaigns.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredCampaigns.length / this.perPage);
    },

    totalItems() {
      return this.filteredCampaigns.length;
    },

    startItem() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },

    endItem() {
      const end = this.currentPage * this.perPage;
      return end > this.totalItems ? this.totalItems : end;
    }
  },

  async created() {
    this.debouncedSearch = debounce(this.handleSearch, 300);
    await this.loadCampaigns();

    // Handle ?edit= query param for draft resumption (from Drafts page)
    if (this.$route.query.edit) {
      this.openDraftForEditing(this.$route.query.edit);
    }
  },

  watch: {
    '$route.query.edit'(newVal) {
      if (newVal) {
        this.openDraftForEditing(newVal);
      }
    }
  },

  methods: {
    async loadCampaigns() {
      this.busy = true;
      try {
        await this.$store.dispatch('campaigns/fetchCampaigns');
      } catch (error) {
        console.error('Error loading campaigns:', error);
      } finally {
        this.busy = false;
      }
    },

    handleSearch() {
      this.currentPage = 1;
    },

    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      this.currentPage = 1;
    },

    getSortIcon(column) {
      if (this.sortField !== column) {
        return 'fa fa-sort';
      }
      return this.sortDirection === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
    },

    setQuickFilter(filter) {
      this.activeQuickFilter = filter;
      this.currentPage = 1;
    },

    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = null;
      this.filterType = null;
      this.filterList = null;
      this.activeQuickFilter = 'all';
      this.currentPage = 1;
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    openCreateCampaignModal() {
      this.selectedCampaign = null;
      this.isEditing = false;
      this.$refs.createCampaignModal.show();
    },

    viewCampaign(campaign) {
      const status = (campaign.Status || '').toLowerCase();
      if (status === 'draft' || status === 'list') {
        this.editCampaign(campaign);
        return;
      }
      this.$router.push({
        name: 'campaignDetail',
        params: { campaignId: campaign.CampaignIDForWeb || campaign.CampaignID }
      });
    },

    async editCampaign(campaign) {
      // Open the modal immediately with a loading overlay, THEN fetch the full
      // campaign data (the list query excludes TextContent/HTMLContent) and populate.
      // Gives instant feedback instead of a silent wait before the popup appears.
      this.isEditing = true;
      this.selectedCampaign = null;
      const modal = this.$refs.createCampaignModal;
      modal.show();
      modal.showLoading('Loading campaign…');
      try {
        const fullCampaign = await this.$store.dispatch(
          'campaigns/fetchCampaignById',
          campaign.CampaignIDForWeb
        );
        this.selectedCampaign = fullCampaign || { ...campaign };
      } catch (e) {
        console.warn('Failed to fetch full campaign, using list data:', e);
        this.selectedCampaign = { ...campaign };
      } finally {
        modal.hideLoading();
      }
    },

    async duplicateCampaign(campaign) {
      try {
        const campaignId = campaign.CampaignIDForWeb || campaign.CampaignID;
        await this.$store.dispatch('campaigns/duplicateCampaign', campaignId);
        this.$toast?.success?.('Campaign duplicated successfully');
        await this.$store.dispatch('campaigns/fetchCampaigns');
      } catch (error) {
        console.error('Error duplicating campaign:', error);
        this.$toast?.error?.('Failed to duplicate campaign');
      }
    },

    // "Send" from the three-dot menu (draft campaigns only). Confirms first — a campaign send
    // places real calls/messages to patients — then launches without opening the editor.
    async confirmSendCampaign(campaign) {
      // The list row omits TextContent/HTMLContent; fetch the full record so the send has everything.
      let full = campaign;
      try {
        full = await this.$store.dispatch('campaigns/fetchCampaignById', campaign.CampaignIDForWeb) || campaign;
      } catch (e) {
        console.warn('Could not fetch full campaign for send; using list data:', e);
      }

      const aiCall = isAiCallCampaign(full);
      const count = campaignAudienceCount(full);
      const typeLabel = aiCall ? 'AI Call' : (full.Type || 'campaign');
      let msg = `Send "${full.Name}" now? This will ${aiCall ? 'place real AI phone calls to' : 'send to'} ${count} recipient${count === 1 ? '' : 's'}.`;
      if (aiCall) msg += ' Real patients on the list will be called by the AI agent.';

      const ok = await this.$bvModal.msgBoxConfirm(msg, {
        title: `Send ${typeLabel} campaign?`,
        okTitle: 'Send now',
        okVariant: aiCall ? 'warning' : 'success',
        cancelTitle: 'Cancel',
        centered: true,
      });
      if (!ok) return;

      try {
        const r = await this.runSendWithGate(buildQuickSendParams(full));
        if (r.sent) {
          this.$toast?.success?.('Campaign is sending…');
          await this.loadCampaigns();
        }
      } catch (error) {
        console.error('Send from menu failed:', error);
        this.$toast?.error?.((error.response && error.response.data && error.response.data.message) || error.message || 'Failed to send campaign');
      }
    },

    confirmDeleteCampaign(campaign) {
      this.campaignToDelete = campaign;
      this.$refs.delete.show();
    },

    async deleteCampaign() {
      if (!this.campaignToDelete) return;

      try {
        const campaignId = this.campaignToDelete.CampaignIDForWeb || this.campaignToDelete.CampaignID;
        await this.$store.dispatch('campaigns/deleteCampaign', campaignId);
        this.$toast.success('Campaign deleted successfully');
        this.$refs.delete.hide();
      } catch (error) {
        console.error('Error deleting campaign:', error);
        this.$toast.error('Failed to delete campaign');
      } finally {
        this.campaignToDelete = null;
      }
    },

    // Dispatch a campaign send; on the 409 confirmation gate — large SEGMENT volume
    // and/or an over-allowance CHARGE — show the right consent UI (the charge needs an
    // explicit checkbox approval) and resend echoing BOTH acks so both server gates
    // clear at once. Returns { sent } (false = the user cancelled). Preserves the
    // caller's payload on the resend. Shared by both quick-send entry points.
    async runSendWithGate(payload) {
      try {
        await this.$store.dispatch('campaigns/sendCampaign', payload);
        return { sent: true };
      } catch (error) {
        // Carrier size hard block (422) → too large for carriers; not confirmable.
        if (error.response?.status === 422 && error.response?.data?.carrierBlocked) {
          await this.$bvModal.msgBoxOk(
            error.response.data.reason || 'This message is too large for mobile carriers to deliver. Please shorten it.',
            { title: 'Message too large to send', okVariant: 'danger', centered: true }
          );
          return { sent: false };
        }
        // Overage charge failed server-side → send was blocked; explain + stop.
        if (error.response?.status === 402 && error.response?.data?.paymentFailed) {
          await this.$bvModal.msgBoxOk(buildPaymentFailedMessage(error.response.data), {
            title: 'Payment needed to send', okVariant: 'warning', centered: true,
          });
          return { sent: false };
        }
        const estimate = error.response?.status === 409 && error.response?.data?.estimate;
        if (!estimate) throw error;
        // Pass the server's credit-wallet consent object (P4c) so quick-send discloses the BULK
        // top-up, identical to the wizard — without it this path would fall back to legacy copy.
        // E3: `walletChannels` (multi-pool) supersedes the single `wallet` when present.
        const g = interpretSendGate(estimate, error.response?.data?.wallet, error.response?.data?.walletChannels);
        let approved;
        if (g.hasOverage) {
          approved = await this.$refs.overageModal.open({
            // WALLET mode: amount = bulk top-up; wallet carries the used-now/rolls-over split.
            // LEGACY mode: amount = overage floored to Stripe's $0.50 min (rawOverage/minimumApplied disclose it).
            amount: g.chargeAmount,
            rawOverage: g.overageAmount,
            minimumApplied: g.minimumApplied,
            chargeableSegments: g.chargeableSegments,
            ratePerSegment: OVERAGE_PRICE_PER_SEGMENT_USD,
            wallet: g.wallet,
            // Multi-pool: per-channel top-ups + combined total (empty/false → single-pool path).
            multiChannel: g.multiChannel,
            channels: g.channels,
            totalTopUpUsd: g.totalTopUpUsd,
          });
        } else {
          approved = await this.$bvModal.msgBoxConfirm(
            buildVolumeConfirmMessage(g),
            { title: 'Confirm large campaign', okTitle: 'Yes, send campaign', cancelTitle: 'Go back', okVariant: 'success', centered: true }
          );
        }
        if (!approved) return { sent: false };
        return this.runSendWithGate({ ...payload, ...g.confirmEcho });
      }
    },

    async sendCampaign(campaign) {
      try {
        const r = await this.runSendWithGate({
          campaignId: campaign.CampaignIDForWeb || campaign.CampaignID,
          campaignType: campaign.Type || 'Email',
        });
        if (r.sent) {
          this.$router.push({
            name: 'campaignDetail',
            params: { campaignId: campaign.CampaignIDForWeb || campaign.CampaignID }
          });
        }
      } catch (error) {
        console.error('Error sending campaign:', error);
        this.$toast?.error?.(error.message || 'Failed to send campaign');
      }
    },

    async onCampaignSaved() {
      await this.loadCampaigns();
    },

    async openDraftForEditing(campaignId) {
      try {
        const campaign = this.campaigns.find(c => c.CampaignIDForWeb === campaignId);
        if (campaign) {
          this.editCampaign(campaign);
        } else {
          // Campaign not in loaded list — fetch it directly
          const result = await this.$store.dispatch('campaigns/fetchCampaignById', campaignId);
          if (result) {
            this.editCampaign(result);
          }
        }
        // Clear the query param so refreshing the page doesn't re-open the modal
        if (this.$route.query.edit) {
          this.$router.replace({ query: {} });
        }
      } catch (error) {
        console.error('Error opening draft for editing:', error);
      }
    },

    getStatusVariant(status) {
      const variants = {
        'draft': 'secondary',
        'list': 'secondary',
        'scheduled': 'warning',
        'active': 'success',
        'sending': 'success',
        'sent': 'info',
        'completed': 'info',
        'failed': 'danger',
        'paused': 'dark'
      };
      return variants[(status || '').toLowerCase()] || 'secondary';
    },

    getTypeBadgeClass(type) {
      const classes = {
        'SMS': 'type-sms',
        'Email': 'type-email',
        'Both': 'type-both'
      };
      return classes[type] || '';
    },

    getTypeIcon(type) {
      const icons = {
        'SMS': 'fa fa-comment',
        'Email': 'fa fa-envelope',
        'Both': 'fa fa-comments'
      };
      return icons[type] || 'fa fa-paper-plane';
    },

    getDeliveryRate(campaign) {
      if (!campaign.SentCount) return 0;
      return ((campaign.DeliveredCount / campaign.SentCount) * 100).toFixed(1);
    },

    getOpenRate(campaign) {
      if (!campaign.DeliveredCount) return 0;
      return ((campaign.OpenCount / campaign.DeliveredCount) * 100).toFixed(1);
    },

    getClickRate(campaign) {
      const clicked = campaign.ClickedCount || 0;
      // Fall back to sent (then total recipients) when DeliveredCount is 0 so real clicks still
      // show a rate — mirrors CampaignDetail.vue clickRate(). SMS delivery receipts can lag or
      // never arrive, which would otherwise pin this at 0.0% despite real clicks.
      const denom = campaign.DeliveredCount || campaign.SentCount || campaign.TotalRecipients || campaign.RecipientCount || 0;
      if (!denom) return '0.0';
      return ((clicked / denom) * 100).toFixed(1);
    },


  },

  filters: {
    formatNumber(value) {
      if (!value) return '0';
      return value.toLocaleString();
    },
    capitalize(value) {
      if (!value) return '';
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  }
};
</script>

<style src="./Campaigns.scss" lang="scss" scoped></style>
